import React, {useMemo} from 'react';
import {Badge, Divider, Icon, Text} from '@albert-io/atomic';

import MarkdownRendererV2 from 'generic/MarkdownRendererV2/MarkdownRendererV2.react';
import type {FrqRubric} from 'lib/rubric/types';

import type {FrqCriterionScore, ManualEvaluation} from './FrqEvaluationSummary.types';

type Props = {
  /** Criteria rubric to break down; null omits the criterion list (unstructured rubrics). */
  rubric: FrqRubric | null;
  evaluation: ManualEvaluation;
};

function TeacherFeedback({feedback}: {feedback: string}) {
  return (
    <div className='gap-x2 rounded-x2 border-border-primary background-subtle p-x2 flex w-full flex-col border'>
      <div className='gap-x1 flex w-full items-center'>
        <Icon
          icon={['far', 'message-lines']}
          size='inherit'
          color='secondary'
          className='flex shrink-0'
        />
        <Text size='m' color='primary'>
          Your teacher’s feedback
        </Text>
      </div>
      <Text size='m' color='primary' className='w-full whitespace-pre-line'>
        {feedback}
      </Text>
    </div>
  );
}

/**
 * Per-criterion score and feedback breakdown for a graded FRQ — the released
 * view a student sees, also mirrored live in the teacher grading panel's
 * preview tab. Non-interactive and chrome-free: callers own the surrounding
 * wrapper and the correctness banner (which carries the overall percent).
 */
export default function FrqEvaluationSummary({rubric, evaluation}: Props) {
  const scoresById = useMemo(() => {
    const map = new Map<string, FrqCriterionScore>();
    evaluation.criteriaScores.forEach((c) => map.set(c.criterionId, c));
    return map;
  }, [evaluation.criteriaScores]);

  const overallFeedback = evaluation.overallFeedback;
  const hasOverallFeedback = typeof overallFeedback === 'string' && overallFeedback.length > 0;

  return (
    <div className='gap-x2 flex flex-col'>
      {hasOverallFeedback ? (
        <div className='px-x2 py-x1-half'>
          <TeacherFeedback feedback={overallFeedback} />
        </div>
      ) : null}

      {rubric?.criteria.map((criterion, index) => {
        const criterionScore = scoresById.get(criterion.id);
        const score = criterionScore?.score ?? 0;
        const feedback = criterionScore?.feedback;
        const hasFeedback = typeof feedback === 'string' && feedback.length > 0;

        return (
          <React.Fragment key={criterion.id}>
            {index > 0 || hasOverallFeedback ? <Divider spacing={0} className='mt-x0' /> : null}
            <div className='gap-x3 px-x2 py-x1-half flex flex-col'>
              <div className='gap-x1-half flex flex-col'>
                <Text bold size='m' color='primary'>
                  {criterion.title}
                </Text>
                {criterion.description ? (
                  // Criterion descriptions are authored markdown (same source as CriterionCard).
                  <MarkdownRendererV2
                    text={criterion.description}
                    className='text-m content-primary'
                  />
                ) : null}
              </div>
              <div className='gap-x1-half flex flex-col items-start'>
                <Badge className='shrink-0 font-bold'>
                  Score: {score}/{criterion.maxScore}
                </Badge>
                {hasFeedback ? <TeacherFeedback feedback={feedback} /> : null}
              </div>
            </div>
          </React.Fragment>
        );
      })}
    </div>
  );
}
